/**
 * 게임 테스트 플레이그라운드 엔진 — 결정 #30(인앱 테스트 환경). 제작자가 만든 게임을
 * 실제 AI 대화(토큰 소모) 없이 격리 실행해 검증한다. "모의 AI 출력"(태그 포함)을
 * 넣으면 parseTurn→applyTurn→viewSpec을 그대로 돌려 내레이션·상태 변화·위젯을
 * 즉시 보여준다. 모든 게임 로직이 순수 함수(결정 #10/#26/#27)라 AI 없이 결정적이다.
 *
 * 이 엔진은 GameModule 계약만 의존하므로 네이티브 모듈과 데이터형 카드(#29)를
 * 구분 없이 검증한다. 주사위 시드를 받아 재현 가능한 결과를 보장한다(결정 #4).
 */
import { parseTurn, type ParsedTurn } from "./turn-protocol.js";
import { sanitizeViewSpec, type ViewSpec } from "./view-spec.js";
import { makeSeededRandom } from "../script/policy.js";
import type { GameModule } from "../ports/game-module.js";

/** 상태 변화 한 항목(테스트 표시용). */
export interface StateDiffEntry {
  key: string;
  before: unknown;
  after: unknown;
  change: "added" | "removed" | "changed";
}

/** dryRunTurn 결과 — 한 턴의 모든 관찰값(결정 #30 디버깅). */
export interface DryRunResult {
  /** 화면/저장에 쓰일 본문(태그 제거 후; 비면 원문 폴백, 결정 #26). */
  narration: string;
  /** 파싱된 구조 출력(선택지·상태연산·주사위·장면). */
  parsed: ParsedTurn;
  /** 턴 적용 전 상태(스냅샷). */
  prevState: Record<string, unknown>;
  /** 턴 적용 후 상태. */
  nextState: Record<string, unknown>;
  /** prev→next 변화 목록(키 단위). */
  diff: StateDiffEntry[];
  /** 게임 종료 여부. */
  ended: boolean;
  /** 엔딩 라벨(있으면). */
  endingLabel?: string;
  /** 적용 후 상태로 선언된 뷰(위젯/HUD). 화이트리스트로 살균됨(결정 #27). */
  viewSpec: ViewSpec;
  /**
   * 적용 후 상태(nextState) 기준으로 모델에게 주입될 지시문(갭2 관측성). 제작자가
   * AI 없이 "이 상태가 되면 어떤 행동 규범(게이트)이 주입되는지"를 확인하게 한다.
   * 모듈이 protocolInstructions를 구현했을 때만 채워진다(자유대화 모듈은 undefined).
   */
  instructions?: string;
}

/**
 * 한 턴을 모의 실행한다(결정 #30). AI를 호출하지 않고 주어진 mockOutput을 그 턴의
 * AI 응답으로 간주해 모듈 훅을 돌린다. applyTurn이 없는 자유대화 모듈은 상태 변화
 * 없이 내레이션만 통과시킨다(결정 #28 가산성).
 *
 * @param seed 주사위 등 RNG 시드(결정 #4). 같은 (상태, 출력, 시드)는 같은 결과.
 */
export function dryRunTurn(
  module: GameModule,
  state: Record<string, unknown>,
  mockOutput: string,
  options: { seed?: number } = {},
): DryRunResult {
  const prevState = clone(state);
  const parsed = parseTurn(mockOutput);
  const seed = options.seed ?? 1;

  if (!module.applyTurn) {
    const viewSpec = sanitizeViewSpec(module.viewSpec?.(prevState));
    const result: DryRunResult = {
      narration: parsed.narration.trim() || mockOutput,
      parsed,
      prevState,
      nextState: prevState,
      diff: [],
      ended: false,
      viewSpec,
    };
    const instructions = module.protocolInstructions?.(prevState);
    if (instructions !== undefined) result.instructions = instructions;
    return result;
  }

  const outcome = module.applyTurn({
    state: prevState,
    parsed,
    random: makeSeededRandom(seed),
  });
  const nextState = outcome.state;
  const viewSpec = sanitizeViewSpec(module.viewSpec?.(nextState));

  const result: DryRunResult = {
    narration: parsed.narration.trim() || mockOutput,
    parsed,
    prevState,
    nextState,
    diff: diffStates(prevState, nextState),
    ended: outcome.ended ?? false,
    viewSpec,
  };
  if (outcome.endingLabel) result.endingLabel = outcome.endingLabel;
  // 적용 후 상태 기준으로 다음 턴에 주입될 지시문(갭2 관측성). viewSpec과 동일 상태.
  const instructions = module.protocolInstructions?.(nextState);
  if (instructions !== undefined) result.instructions = instructions;
  return result;
}

/** 두 상태의 키 단위 차이를 계산한다(추가/삭제/변경). 안정적 정렬. */
export function diffStates(
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
): StateDiffEntry[] {
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  const out: StateDiffEntry[] = [];
  for (const key of keys) {
    const inPrev = key in prev;
    const inNext = key in next;
    const before = prev[key];
    const after = next[key];
    if (inPrev && !inNext) {
      out.push({ key, before, after: undefined, change: "removed" });
    } else if (!inPrev && inNext) {
      out.push({ key, before: undefined, after, change: "added" });
    } else if (!eq(before, after)) {
      out.push({ key, before, after, change: "changed" });
    }
  }
  return out.sort((a, b) => a.key.localeCompare(b.key));
}

/** 값 동등성(JSON 직렬화 비교 — 상태는 직렬화 가능, 결정 #28). */
function eq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
